import { FC, ReactElement, useCallback } from "react";
import { MeasurementSystem } from "src/js/stores/user/store";
import { isEqual } from "lodash";
import { PriceModes } from "../../typings/PriceModes";
import {
  HMLRComparablesFilterOptions,
  HomeTrackComparablesFilterOptions,
  LegacyComparablesFilterOptions,
} from "../../typings/Comparables";
import { Theme } from "react-migration/lib/theme/Theme";
import { DeprecatedTooltip } from "react-migration/components/DeprecatedTooltip";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { DCSCondition } from "react-migration/lib/typings/comparables/dcs";

type ComparablesFilterOptions = HMLRComparablesFilterOptions | HomeTrackComparablesFilterOptions;
type DCS = NonNullable<ComparablesFilterOptions["dcsConditions"]>;

export const useFiltersUpdate = (
  filters: ComparablesFilterOptions,
  setFilters: (filters: ComparablesFilterOptions) => void,
  selectedDCSConditions: DCSCondition[],
  eventTracking?: (values: { filterName: string; filterState: any }) => void
) => {
  return useCallback(
    (val: Partial<ComparablesFilterOptions>) => {
      if (eventTracking) {
        for (const [key, value] of Object.entries(val)) {
          eventTracking({ filterName: key, filterState: value });
        }
      }
      const updatedFilters = {
        ...filters,
        dcsConditions: selectedDCSConditions as DCS,
        ...val,
      } as ComparablesFilterOptions;

      if (!isEqual(updatedFilters, filters)) {
        setFilters(updatedFilters);
      }
    },
    [filters, setFilters, eventTracking, selectedDCSConditions]
  );
};

export interface FiltersProps<
  TFields extends string,
  TFilters extends HMLRComparablesFilterOptions | HomeTrackComparablesFilterOptions | LegacyComparablesFilterOptions
> {
  disabled?: boolean;
  disabledFields?: Set<TFields>;
  hiddenFields?: Set<TFields>;
  priceMode: PriceModes;
  filters: TFilters;
  updateFilters: (val: Partial<TFilters>) => void;
  selectedDCSConditions: DCSCondition[];
  unitPreference: MeasurementSystem;
  theme?: Theme;
}

export const DisabledMessage: FC<{
  children: ReactElement;
  enableTooltip?: boolean;
  description?: JSX.Element;
}> = ({ children, description, enableTooltip }) => {
  const { t } = useTranslation();
  if (enableTooltip) {
    const defaultDescription = t("comparables.map.sidebar.filters.filter_disabled_for_zoopla_line_1");

    return (
      <DeprecatedTooltip
        theme={Theme.Dark}
        placement="top"
        description={description || defaultDescription}
        className="flex flex-col align-middle"
      >
        {children}
      </DeprecatedTooltip>
    );
  }

  return children ?? null;
};
